<template>
  <div class="portal">
    <!-- 顶部导航 -->
    <div class="header">
      <div class="header-left">
        <div class="logo-mark" aria-label="ARISHEIN Logo">
          <img src="../assets/logo.png" alt="ARISHEIN" />
        </div>
        <div class="logo-text">
          <div class="logo-title">ARISHEIN 企业门户</div>
          <div class="logo-subtitle">Enterprise Portal</div>
        </div>
      </div>

      <div class="header-right">
        <div class="header-time">{{ currentDateText }}</div>

        <div class="user-box" @click="openProfileDialog">
          <div class="user-avatar-wrap">
            <img
              v-if="profile.avatarUrl"
              :src="profile.avatarUrl"
              class="user-avatar-img"
              alt="头像"
            />
            <div v-else class="user-avatar">{{ userInitial }}</div>
          </div>
          <div class="user-info">
            <div class="user-name">{{ profile.displayName || '—' }}</div>
            <div class="user-role">{{ metaLine }}</div>
          </div>
        </div>

        <button class="logout-btn" @click="handleLogout">退出</button>
      </div>
    </div>

    <el-dialog
      title="个人信息"
      :visible.sync="profileDialogVisible"
      width="520px"
      :close-on-click-modal="false"
      custom-class="profile-dialog"
      append-to-body
    >
      <div class="profile-panel">
        <div class="profile-avatar-section">
          <div class="profile-avatar-large">
            <img v-if="profile.avatarUrl" :src="profile.avatarUrl" alt="头像" />
            <span v-else>{{ userInitial }}</span>
          </div>
          <div class="profile-avatar-actions">
            <div class="profile-display-name">{{ profile.displayName || '—' }}</div>
            <div class="profile-meta-line">{{ metaLine }}</div>
            <button
              type="button"
              class="avatar-upload-btn"
              :disabled="avatarUploading"
              @click="triggerAvatarSelect"
            >
              {{ avatarUploading ? '上传中...' : '修改头像' }}
            </button>
            <input
              ref="avatarInput"
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
              class="avatar-file-input"
              @change="handleAvatarFileChange"
            />
          </div>
        </div>

        <div class="profile-info-grid">
          <div class="profile-info-item">
            <label>登录账号</label>
            <span>{{ profile.userId || '—' }}</span>
          </div>
          <div class="profile-info-item">
            <label>员工编号</label>
            <span>{{ profile.empId || '—' }}</span>
          </div>
          <div class="profile-info-item">
            <label>中文姓名</label>
            <span>{{ profile.displayName || '—' }}</span>
          </div>
          <div class="profile-info-item">
            <label>英文姓名</label>
            <span>{{ profile.englishName || '—' }}</span>
          </div>
          <div class="profile-info-item">
            <label>所属部门</label>
            <span>{{ profile.deptName || '—' }}</span>
          </div>
          <div class="profile-info-item">
            <label>职位</label>
            <span>{{ profile.positionName || '—' }}</span>
          </div>
          <div class="profile-info-item">
            <label>角色</label>
            <span>{{ profile.roleText || '—' }}</span>
          </div>
          <div class="profile-info-item">
            <label>手机号</label>
            <span>{{ profile.phoneNumber || '—' }}</span>
          </div>
          <div class="profile-info-item profile-info-item--wide">
            <label>邮箱</label>
            <span>{{ profile.email || '—' }}</span>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 主体 -->
    <div class="content">

      <!-- 系统入口 -->
      <div class="section-header">
        <div>
          <h2>系统入口</h2>
          <p>统一进入公司各业务系统</p>
        </div>
      </div>

      <div class="card-grid">
        <div v-if="hasMenu('/crm-index')" class="system-card crm" @click="go('/crm-index')">
          <div class="card-top">
            <div class="card-icon">📊</div>
            <div class="card-arrow">→</div>
          </div>
          <h3>CRM(客户关系管理）</h3>
          <p>客户管理、线索跟进、商机转化、项目推进</p>
          <div class="card-tags">
            <span>客户</span>
            <span>销售</span>
            <span>项目</span>
          </div>
        </div>

        <div v-if="hasMenu('/finance-index')" class="system-card finance">
          <div class="card-top">
            <div class="card-icon">💰</div>
            <div class="card-arrow">→</div>
          </div>
          <h3>报销系统</h3>
          <p>费用申请、报销审批、财务流转、凭证留痕</p>
          <div class="card-tags">
            <span>报销</span>
            <span>审批</span>
            <span>财务</span>
          </div>
        </div>

        <div v-if="hasMenu('/asset-index')" class="system-card asset" @click="go('/asset-index')">
          <div class="card-top">
            <div class="card-icon">🖥️</div>
            <div class="card-arrow">→</div>
          </div>
          <h3>ARM(资产管理)</h3>
          <p>固定资产、办公设备、领用归还、生命周期管理</p>
          <div class="card-tags">
            <span>资产</span>
            <span>设备</span>
            <span>管理</span>
          </div>
        </div>

        <div v-if="hasMenu('/book-index')" class="system-card book" @click="go('/book-index')">
          <div class="card-top">
            <div class="card-icon">📚</div>
            <div class="card-arrow">→</div>
          </div>
          <h3>LMS（图书管理系统）</h3>
          <p>图书借阅、库存统计、分类检索、归还提醒</p>
          <div class="card-tags">
            <span>图书</span>
            <span>借阅</span>
            <span>库存</span>
          </div>
        </div>

        <div v-if="hasMenu('/expo-index')" class="system-card expo">
          <div class="card-top">
            <div class="card-icon">🎪</div>
            <div class="card-arrow">→</div>
          </div>
          <h3>展会系统</h3>
          <p>展会计划、参展信息、客户收集、成果复盘</p>
          <div class="card-tags">
            <span>展会</span>
            <span>客户</span>
            <span>活动</span>
          </div>
        </div>

        <div v-if="hasMenu('/srm-index')" class="system-card srm" @click="go('/srm-index')">
          <div class="card-top">
            <div class="card-icon">🤝</div>
            <div class="card-arrow">→</div>
          </div>
          <h3>SRM(供应商关系管理)</h3>
          <p>供应商准入、询比价、订单协同、对账与绩效评估</p>
          <div class="card-tags">
            <span>供应商</span>
            <span>采购</span>
            <span>协同</span>
          </div>
        </div>

        <div v-if="hasMenu('/kms-index')" class="system-card kms" @click="go('/kms-index')">
          <div class="card-top"><div class="card-icon">📘</div><div class="card-arrow">→</div></div>
          <h3>KMS(知识管理)</h3>
          <p>政策制度、学习资料、业务文件统一发布与在线查阅</p>
          <div class="card-tags"><span>知识</span><span>学习</span><span>文档</span></div>
        </div>

        <div v-if="hasMenu('/more')" class="system-card more">
          <div class="card-top">
            <div class="card-icon">⚙️</div>
            <div class="card-arrow">→</div>
          </div>
          <h3>更多系统</h3>
          <p>后续可接入 ERP、OA、审批流、合同管理等模块</p>
          <div class="card-tags">
            <span>ERP</span>
            <span>OA</span>
            <span>扩展</span>
          </div>
        </div>
      </div>

      <!-- 中部信息区 -->
      <div class="middle-grid">
        <div class="panel quick-panel">
          <div class="panel-header">
            <h4>⚡ 快捷操作</h4>
            <span>常用入口</span>
          </div>

          <div class="quick-actions">
            <div v-if="hasMenu('/crm-index')" class="quick-item" @click="go('/crm-index')">
              <div class="quick-icon">👤</div>
              <div class="quick-text">客户管理</div>
            </div>
            <div v-if="hasMenu('/crm-index')" class="quick-item" @click="go('/crm-index')">
              <div class="quick-icon">🧲</div>
              <div class="quick-text">线索管理</div>
            </div>
            <div v-if="hasMenu('/crm-index')" class="quick-item" @click="go('/crm-index')">
              <div class="quick-icon">📊</div>
              <div class="quick-text">CRM驾驶舱</div>
            </div>
            <div v-if="hasMenu('/crm-index')" class="quick-item" @click="go('/crm-index')">
              <div class="quick-icon">📁</div>
              <div class="quick-text">项目一览</div>
            </div>
          </div>
        </div>

        <div class="panel notice-panel">
          <div class="panel-header">
            <h4>📢 公告通知</h4>
            <span class="notice-more" @click="goNotices">查看更多 →</span>
          </div>

          <div class="notice-list">
            <div
              v-for="item in latestNotices"
              :key="item.id"
              class="notice-item"
              @click="openNotice(item.id)"
            >
              <div class="notice-dot" :class="{ 'notice-dot--top': item.isTop }"></div>
              <div class="notice-main">
                <div class="notice-title">{{ item.title }}</div>
                <div class="notice-time">{{ formatNoticeDate(item.publishTime) }}</div>
              </div>
            </div>
            <div v-if="noticeLoaded && !latestNotices.length" class="notice-empty">暂无公告</div>
          </div>
        </div>
      </div>

      <!-- 底部信息区 -->
      <div class="bottom-grid">
        <div class="panel todo-panel">
          <div class="panel-header todo-panel-header">
            <div class="todo-tabs">
              <button
                type="button"
                class="todo-tab"
                :class="{ active: todoTab === 'pending' }"
                @click="switchTodoTab('pending')"
              >
                我的待办
                <span v-if="todoCounts.pending > 0" class="todo-count-badge">
                  {{ formatTodoCount(todoCounts.pending) }}
                </span>
              </button>
              <button
                type="button"
                class="todo-tab"
                :class="{ active: todoTab === 'done' }"
                @click="switchTodoTab('done')"
              >
                已办
                <span v-if="todoCounts.done > 0" class="todo-count-badge">
                  {{ formatTodoCount(todoCounts.done) }}
                </span>
              </button>
              <button
                type="button"
                class="todo-tab"
                :class="{ active: todoTab === 'submitted' }"
                @click="switchTodoTab('submitted')"
              >
                提交
                <span v-if="todoCounts.submitted > 0" class="todo-count-badge">
                  {{ formatTodoCount(todoCounts.submitted) }}
                </span>
              </button>
              <button
                type="button"
                class="todo-tab"
                :class="{ active: todoTab === 'cc' }"
                @click="switchTodoTab('cc')"
              >
                抄送我的
                <span v-if="todoCounts.cc > 0" class="todo-count-badge">
                  {{ formatTodoCount(todoCounts.cc) }}
                </span>
              </button>
            </div>
            <span>{{ todoTabHint }}</span>
          </div>

          <div class="todo-list">
            <div
              v-for="item in todoList"
              :key="item.id"
              class="todo-item"
              @click="openApprovalTodo(item)"
            >
              <div class="todo-left">
                <div class="todo-badge" :class="todoBadgeClass"></div>
                <span>{{ item.title }}</span>
              </div>
              <div class="todo-right">共{{ item.count }}笔</div>
            </div>
            <div v-if="todoLoaded && !todoList.length" class="todo-empty">
              {{ todoEmptyText }}
            </div>
          </div>
        </div>

        <div class="panel schedule-panel">
          <div class="panel-header">
            <h4>📅 今日安排</h4>
            <span>工作日程</span>
          </div>

          <div class="schedule-list">
            <div class="todo-empty">暂无安排</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { clearAllAuth } from '@/utils/auth'
import request, { parseApiResponse } from '@/utils/request'
import { setMenuPermissions, hasMenuForRoute } from '@/utils/menuPermission'

export default {
  name: 'PortalPage',
  data() {
    return {
      currentDateText: '',
      profile: {
        userId: '',
        empId: '',
        displayName: '',
        englishName: '',
        phoneNumber: '',
        email: '',
        avatarUrl: null,
        deptName: '',
        positionName: '',
        roleText: ''
      },
      portalMenus: [],
      menuLoaded: false,
      latestNotices: [],
      noticeLoaded: false,
      todoList: [],
      todoLoaded: false,
      todoTab: 'pending',
      todoCounts: {
        pending: 0,
        done: 0,
        submitted: 0,
        cc: 0
      },
      profileDialogVisible: false,
      avatarUploading: false
    }
  },
  computed: {
    userInitial() {
      const n = (this.profile.displayName || this.profile.userId || '').trim()
      if (!n) return '?'
      return n.charAt(0).toUpperCase()
    },
    metaLine() {
      const parts = []
      if (this.profile.positionName) parts.push(this.profile.positionName)
      if (this.profile.roleText) parts.push(this.profile.roleText)
      return parts.length ? parts.join(' · ') : '—'
    },
    todoTabHint() {
      if (this.todoTab === 'done') return '我已处理的事项'
      if (this.todoTab === 'submitted') return '我发起的审批'
      if (this.todoTab === 'cc') return '抄送给我的审批'
      return '需要尽快处理'
    },
    todoEmptyText() {
      if (this.todoTab === 'done') return '暂无已办'
      if (this.todoTab === 'submitted') return '暂无提交'
      if (this.todoTab === 'cc') return '暂无抄送'
      return '暂无待办'
    },
    todoBadgeClass() {
      if (this.todoTab === 'done') return 'green'
      if (this.todoTab === 'submitted') return 'orange'
      if (this.todoTab === 'cc') return 'purple'
      return 'blue'
    }
  },
  mounted() {
    this.updateCurrentDate()
    this.loadProfile()
    this.loadMyMenus()
    this.loadLatestNotices()
    this.loadTodoList()
    this.loadTodoCounts()
  },
  activated() {
    this.loadTodoList()
    this.loadTodoCounts()
  },
  methods: {
    async loadMyMenus() {
      try {
        const res = await request.get('/api/portal/my/menus')
        const data = parseApiResponse(res);
          this.portalMenus = data || []
          setMenuPermissions(this.portalMenus)
        
      } catch (e) {
        this.portalMenus = []
        setMenuPermissions([])
      } finally {
        this.menuLoaded = true
      }
    },
    hasMenu(routePath) {
      if (!this.menuLoaded) return false
      return hasMenuForRoute(routePath)
    },
    async loadProfile() {
      try {
        const res = await request.get('/api/portal/profile')
        const data = parseApiResponse(res);
        this.profile = {
          userId: data.userId || '',
          empId: data.empId || '',
          displayName: data.displayName || '',
          englishName: data.englishName || '',
          phoneNumber: data.phoneNumber || '',
          email: data.email || '',
          avatarUrl: data.avatarUrl || null,
          deptName: data.deptName || '',
          positionName: data.positionName || '',
          roleText: data.roleText || ''
        }
      } catch (e) {
        /* 未登录等由 request 拦截器处理 */
      }
    },
    go(path) {
      this.$router.push(path)
    },
    async loadLatestNotices() {
      try {
        const res = await request.get('/api/portal/announcements/latest', { params: { limit: 3 } })
        const data = parseApiResponse(res);
          this.latestNotices = Array.isArray(data) ? data : []
        
      } catch (e) {
        console.error('加载公告失败', e)
        this.latestNotices = []
      } finally {
        this.noticeLoaded = true
      }
    },
    async loadTodoList() {
      this.todoLoaded = false
      try {
        let endpoint = '/api/workflow/tasks/pending/groups'
        if (this.todoTab === 'done') {
          endpoint = '/api/workflow/tasks/done/groups'
        } else if (this.todoTab === 'submitted') {
          endpoint = '/api/workflow/instances/submitted/groups'
        } else if (this.todoTab === 'cc') {
          endpoint = '/api/workflow/cc/groups'
        }
        const res = await request.get(endpoint)
        const data = parseApiResponse(res)
        const list = Array.isArray(data) ? data : []
        this.todoList = list.slice(0, 5).map(item => ({
          id: `${item.bizType || 'UNKNOWN'}-${item.actionType || 'APPROVE'}`,
          bizType: item.bizType || '',
          actionType: item.actionType || '',
          title: item.title || `${item.bizTypeName || '业务'}${item.actionTypeName || '审批'}`,
          count: item.count || 0
        }))
      } catch (e) {
        const label = this.todoTab === 'done'
          ? '已办'
          : (this.todoTab === 'submitted' ? '提交' : (this.todoTab === 'cc' ? '抄送' : '待办'))
        console.error(`加载${label}失败`, e)
        this.todoList = []
      } finally {
        this.todoLoaded = true
      }
    },
    async loadTodoCounts() {
      try {
        const [pendingRes, doneRes, submittedRes, ccRes] = await Promise.all([
          request.get('/api/workflow/tasks/pending/groups'),
          request.get('/api/workflow/tasks/done/groups'),
          request.get('/api/workflow/instances/submitted/groups'),
          request.get('/api/workflow/cc/groups')
        ])
        const pendingGroups = parseApiResponse(pendingRes)
        const doneGroups = parseApiResponse(doneRes)
        const submittedGroups = parseApiResponse(submittedRes)
        const ccGroups = parseApiResponse(ccRes)
        this.todoCounts = {
          pending: this.sumTodoGroupCount(pendingGroups),
          done: this.sumTodoGroupCount(doneGroups),
          submitted: this.sumTodoGroupCount(submittedGroups),
          cc: this.sumTodoGroupCount(ccGroups)
        }
      } catch (e) {
        console.error('加载审批数量失败', e)
      }
    },
    sumTodoGroupCount(groups) {
      if (!Array.isArray(groups)) return 0
      return groups.reduce((total, item) => total + Number((item && item.count) || 0), 0)
    },
    formatTodoCount(count) {
      const value = Number(count || 0)
      return value > 99 ? '99+' : String(value)
    },
    switchTodoTab(tab) {
      if (this.todoTab === tab) {
        return
      }
      this.todoTab = tab
      this.loadTodoList()
      this.loadTodoCounts()
    },
    openApprovalTodo(item) {
      const mode = this.todoTab === 'done'
        ? 'done'
        : (this.todoTab === 'submitted' ? 'submitted' : (this.todoTab === 'cc' ? 'cc' : 'pending'))
      this.$router.push({
        path: '/portal/approvals',
        query: {
          bizType: item.bizType || '',
          actionType: item.actionType || '',
          title: item.title || '',
          mode
        }
      })
    },
    formatNoticeDate(v) {
      if (!v) return ''
      return String(v).slice(0, 10)
    },
    goNotices() {
      this.$router.push('/portal/notices')
    },
    openNotice(id) {
      if (!id) return
      this.$router.push({ path: '/portal/notices', query: { id: String(id) } })
    },
    openProfileDialog() {
      this.profileDialogVisible = true
    },
    triggerAvatarSelect() {
      if (this.avatarUploading) return
      if (this.$refs.avatarInput) {
        this.$refs.avatarInput.click()
      }
    },
    async handleAvatarFileChange(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      if (!/^image\//i.test(file.type || '')) {
        this.$message.warning('请选择图片文件')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$message.warning('头像文件不能超过 5MB')
        return
      }
      const formData = new FormData()
      formData.append('file', file)
      this.avatarUploading = true
      try {
        const res = await request.post('/api/portal/profile/avatar', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        const data = parseApiResponse(res)
        this.profile = {
          ...this.profile,
          ...(data || {}),
          avatarUrl: data && data.avatarUrl ? `${data.avatarUrl}?t=${Date.now()}` : null
        }
        this.$message.success('头像已更新')
      } catch (err) {
        console.error('上传头像失败', err)
        this.$message.error('头像上传失败')
      } finally {
        this.avatarUploading = false
      }
    },
    updateCurrentDate() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      this.currentDateText = `${year}-${month}-${day}`
    },
    handleLogout() {
      const confirmed = window.confirm('确定要退出登录吗？')
      if (!confirmed) return

      clearAllAuth()
      this.$router.replace('/login')
    }
  }
}
</script>

<style scoped>
.portal {
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(64, 158, 255, 0.12), transparent 28%),
    radial-gradient(circle at top right, rgba(0, 220, 180, 0.1), transparent 24%),
    linear-gradient(135deg, #08131f 0%, #10293a 35%, #1d4253 100%);
  color: #fff;
}

.header {
  height: 72px;
  padding: 0 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(5, 15, 24, 0.45);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-mark {
  width: 54px;
  height: 54px;
  flex: 0 0 54px;
  overflow: hidden;
  position: relative;
  filter: drop-shadow(0 6px 14px rgba(47, 122, 255, 0.38));
}

.logo-mark img {
  position: absolute;
  width: 180px;
  height: 90px;
  max-width: none;
  left: -1px;
  top: -14px;
  object-fit: contain;
}

.logo-title {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.logo-subtitle {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.62);
  margin-top: 2px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 18px;
}

.header-time {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.76);
}

.user-box {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.08);
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.user-box:hover {
  background: rgba(255, 255, 255, 0.14);
  transform: translateY(-1px);
}

.user-avatar-wrap {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
}

.user-avatar-img {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.user-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #24c6dc, #514a9d);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
}

.user-role {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
  margin-top: 2px;
}

.profile-panel {
  color: #1f2d3d;
}

.profile-avatar-section {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 4px 0 22px;
  border-bottom: 1px solid #edf1f7;
}

.profile-avatar-large {
  width: 86px;
  height: 86px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
  background: linear-gradient(135deg, #24c6dc, #514a9d);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 30px;
  font-weight: 700;
  box-shadow: 0 10px 24px rgba(31, 45, 61, 0.14);
}

.profile-avatar-large img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.profile-avatar-actions {
  min-width: 0;
}

.profile-display-name {
  font-size: 20px;
  line-height: 1.4;
  font-weight: 700;
  color: #1f2d3d;
}

.profile-meta-line {
  margin-top: 4px;
  font-size: 13px;
  color: #7b8794;
}

.avatar-upload-btn {
  margin-top: 12px;
  height: 34px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: #409eff;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}

.avatar-upload-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.avatar-file-input {
  display: none;
}

.profile-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 18px;
  padding-top: 20px;
}

.profile-info-item {
  min-width: 0;
}

.profile-info-item--wide {
  grid-column: 1 / -1;
}

.profile-info-item label {
  display: block;
  margin-bottom: 6px;
  color: #8a97a8;
  font-size: 12px;
}

.profile-info-item span {
  display: block;
  min-height: 22px;
  color: #1f2d3d;
  font-size: 14px;
  line-height: 1.55;
  word-break: break-word;
}

.logout-btn {
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #ff7b7b, #f25f5f);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(242, 95, 95, 0.28);
  transition: all 0.25s ease;
}

.logout-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 26px rgba(242, 95, 95, 0.36);
}

.content {
  padding: 28px 32px 36px;
}

.hero {
  display: grid;
  grid-template-columns: 1.6fr 0.9fr;
  gap: 22px;
  margin-bottom: 22px;
}

.hero-left,
.hero-right .summary-card,
.panel,
.system-card,
.stat-card {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 14px 35px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(10px);
}

.hero-left {
  border-radius: 22px;
  padding: 30px;
  position: relative;
  overflow: hidden;
}

.hero-left::after {
  content: '';
  position: absolute;
  right: -60px;
  top: -60px;
  width: 180px;
  height: 180px;
  background: radial-gradient(circle, rgba(64, 158, 255, 0.28), transparent 70%);
}

.hero-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(64, 158, 255, 0.16);
  color: #8fd0ff;
  font-size: 12px;
  margin-bottom: 16px;
}

.hero-title {
  margin: 0;
  font-size: 34px;
  line-height: 1.25;
  font-weight: 700;
}

.hero-desc {
  margin-top: 16px;
  max-width: 680px;
  font-size: 15px;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.78);
}

.hero-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.primary-btn,
.ghost-btn {
  height: 42px;
  border-radius: 12px;
  padding: 0 18px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.25s ease;
}

.primary-btn {
  background: linear-gradient(135deg, #2f8cff, #5c7cff);
  color: #fff;
  box-shadow: 0 10px 24px rgba(47, 122, 255, 0.3);
}

.primary-btn:hover {
  transform: translateY(-2px);
}

.ghost-btn {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.ghost-btn:hover {
  background: rgba(255, 255, 255, 0.14);
}

.summary-card {
  border-radius: 22px;
  padding: 24px;
  height: 100%;
  box-sizing: border-box;
}

.summary-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 18px;
}

.summary-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
}

.summary-item span {
  color: rgba(255, 255, 255, 0.72);
  font-size: 14px;
}

.summary-item strong {
  font-size: 22px;
  color: #fff;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 26px;
}

.stat-card {
  border-radius: 18px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 14px;
}

.stat-icon {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.stat-icon.blue {
  background: rgba(64, 158, 255, 0.18);
}

.stat-icon.cyan {
  background: rgba(0, 214, 201, 0.18);
}

.stat-icon.orange {
  background: rgba(255, 153, 102, 0.18);
}

.stat-icon.green {
  background: rgba(86, 171, 47, 0.18);
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.stat-label {
  margin-top: 6px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: end;
  margin-bottom: 16px;
}

.section-header h2 {
  margin: 0;
  font-size: 24px;
}

.section-header p {
  margin: 6px 0 0;
  color: rgba(255, 255, 255, 0.66);
  font-size: 13px;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
}

.system-card {
  position: relative;
  padding: 22px;
  border-radius: 20px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;
  min-height: 158px;
  box-sizing: border-box;
}

.system-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.24);
}

.system-card::after {
  content: '';
  position: absolute;
  right: -30px;
  top: -30px;
  width: 120px;
  height: 120px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50%;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  position: relative;
  z-index: 1;
}

.card-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.card-arrow {
  font-size: 20px;
  color: rgba(255, 255, 255, 0.88);
}

.system-card h3 {
  margin: 0;
  font-size: 24px;
  position: relative;
  z-index: 1;
}

.system-card p {
  margin: 10px 0 16px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.88);
  font-size: 14px;
  position: relative;
  z-index: 1;
}

.card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  position: relative;
  z-index: 1;
}

.card-tags span {
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  background: rgba(255, 255, 255, 0.15);
}

.crm {
  background: linear-gradient(135deg, #25c6da, #5b7cff);
}

.finance {
  background: linear-gradient(135deg, #ff9966, #ff5e62);
}

.asset {
  background: linear-gradient(135deg, #56ab2f, #a8e063);
}

.book {
  background: linear-gradient(135deg, #614385, #516395);
}

.expo {
  background: linear-gradient(135deg, #11998e, #38ef7d);
}

.srm {
  background: linear-gradient(135deg, #ea580c, #f59e0b);
}

.kms {
  background: linear-gradient(135deg, #3156d9, #7c5ce5);
}

.more {
  background: linear-gradient(135deg, #3a5060, #5c7a8a);
}

.middle-grid,
.bottom-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-top: 22px;
}

.panel {
  border-radius: 18px;
  padding: 20px;
  box-sizing: border-box;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.panel-header h4 {
  margin: 0;
  font-size: 18px;
}

.panel-header span {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.62);
}

.quick-actions {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}

.quick-item {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.25s ease;
}

.quick-item:hover {
  background: rgba(255, 255, 255, 0.12);
  transform: translateY(-3px);
}

.quick-icon {
  font-size: 22px;
  margin-bottom: 10px;
}

.quick-text {
  font-size: 14px;
  font-weight: 500;
}

.notice-list,
.todo-list,
.schedule-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.notice-more {
  cursor: pointer;
  color: #7ec8ff;
  font-size: 13px;
  transition: opacity 0.2s;
}

.notice-more:hover {
  opacity: 0.85;
}

.notice-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: background 0.2s;
}

.notice-item:hover {
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
}

.notice-empty {
  padding: 12px 0;
  color: rgba(255, 255, 255, 0.45);
  font-size: 13px;
}

.todo-empty {
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.55);
  font-size: 14px;
}

.todo-panel-header {
  align-items: center;
}

.todo-tabs {
  display: flex;
  align-items: center;
  gap: 18px;
}

.todo-tab {
  position: relative;
  padding: 0;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.55);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: color 0.2s ease;
}

.todo-count-badge {
  position: absolute;
  top: -11px;
  right: -13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  box-sizing: border-box;
  border: 2px solid #304457;
  border-radius: 10px;
  background: #f56c6c;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  box-shadow: 0 2px 6px rgba(245, 108, 108, 0.35);
  pointer-events: none;
}

.todo-tab.active {
  color: #fff;
}

.todo-tab:not(.active):hover {
  color: rgba(255, 255, 255, 0.85);
}

.notice-dot--top {
  background: #e6a23c;
  box-shadow: 0 0 8px rgba(230, 162, 60, 0.5);
}

.notice-item:last-child {
  border-bottom: none;
}

.notice-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4fc3ff;
  margin-top: 7px;
  box-shadow: 0 0 10px rgba(79, 195, 255, 0.8);
}

.notice-main {
  flex: 1;
}

.notice-title {
  font-size: 14px;
  line-height: 1.6;
}

.notice-time {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
}

.todo-item,
.schedule-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
}

.todo-item {
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;
}

.todo-item:hover {
  background: rgba(255, 255, 255, 0.11);
  transform: translateY(-2px);
}

.todo-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.todo-badge {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.todo-badge.blue {
  background: #4fc3ff;
}

.todo-badge.orange {
  background: #ff9f43;
}

.todo-badge.green {
  background: #34d399;
}

.todo-badge.purple {
  background: #a78bfa;
}

.todo-right {
  font-weight: 700;
  color: #fff;
}

.schedule-item {
  align-items: flex-start;
  gap: 14px;
}

.schedule-time {
  min-width: 58px;
  font-size: 14px;
  font-weight: 700;
  color: #8fd0ff;
}

.schedule-title {
  font-size: 14px;
  font-weight: 600;
}

.schedule-desc {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.62);
  line-height: 1.6;
}

@media (max-width: 1400px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .card-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .hero {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 992px) {
  .middle-grid,
  .bottom-grid,
  .card-grid,
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .header {
    padding: 0 18px;
  }

  .content {
    padding: 20px 18px 28px;
  }
}

@media (max-width: 768px) {
  .header {
    height: auto;
    padding-top: 14px;
    padding-bottom: 14px;
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .header-right {
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
  }

  .hero-title {
    font-size: 26px;
  }

  .hero-actions {
    flex-direction: column;
  }

  .primary-btn,
  .ghost-btn {
    width: 100%;
  }
}
</style>
